import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import ticketsService from "@/services/hrm/ticketsService";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const ticketListCache = createScopedCache("tickets:list");
const ticketDetailCache = createScopedCache("tickets:detail");

const TICKETS_LIST_TTL = 60 * 1000;
const TICKETS_DETAIL_TTL = 2 * 60 * 1000;

export const useTicketStore = defineStore("tickets", () => {
  const items = ref([]);
  const item = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const pagination = reactive({ current_page: 1, per_page: 25, total: 0 });
  const byId = ref({});

  const filters = reactive({
    search: "",
    status: "",
    priority: "",
    category_id: "",
    assignee_id: "",
    requester_id: "",
    date_from: "",
    date_to: "",
    per_page: 25,
    page: 1,
  });

  function toYMD(d) {
    if (!d) return null;
    if (typeof d === "string") return d;
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function currentListParams(params = {}) {
    return { ...filters, ...params };
  }

  function hydrateList(payload) {
    const rows = payload.items || [];
    items.value = rows;
    if (payload.meta) {
      pagination.current_page = payload.meta.current_page;
      pagination.per_page = payload.meta.per_page;
      pagination.total = payload.meta.total;
    } else {
      pagination.current_page = 1;
      pagination.per_page = filters.per_page;
      pagination.total = rows.length;
    }
    byId.value = rows.reduce((acc, row) => {
      if (row?.id != null) acc[row.id] = row;
      return acc;
    }, {});
  }

  async function fetchAll(params = {}, options = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_TICKETS))) {
      items.value = [];
      pagination.current_page = 1;
      pagination.per_page = 25;
      pagination.total = 0;
      return { items: [], meta: null };
    }

    const merged = currentListParams(params);
    loading.value = true;
    error.value = null;
    try {
      return await ticketListCache.get(merged, {
        ttlMs: TICKETS_LIST_TTL,
        force: options.force === true,
        loader: async () => {
          const res = await ticketsService.getAll(merged);
          const data = res.data;
          return {
            items: data.data ?? data ?? [],
            meta: data.meta || null,
          };
        },
        onHydrate: hydrateList,
      });
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to fetch tickets";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureList(params = {}, options = {}) {
    return await fetchAll(params, { ...options, force: false });
  }

  async function fetchOne(id, options = {}) {
    const ticketId = Number(id) || id;
    loading.value = true;
    error.value = null;
    try {
      const payload = await ticketDetailCache.get(
        { id: ticketId },
        {
          ttlMs: TICKETS_DETAIL_TTL,
          force: options.force === true,
          loader: async () => {
            const res = await ticketsService.get(ticketId);
            return { ticket: res.data };
          },
          onHydrate: ({ ticket }) => {
            item.value = ticket || null;
            if (ticket?.id != null) {
              byId.value = { ...byId.value, [ticket.id]: ticket };
            }
          },
        }
      );
      return payload.ticket || null;
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to fetch ticket";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureOne(id, options = {}) {
    return await fetchOne(id, { ...options, force: false });
  }

  function invalidateTicketCaches(id = null) {
    ticketListCache.clear();
    if (id != null) {
      ticketDetailCache.invalidate({ id: Number(id) || id });
    } else {
      ticketDetailCache.clear();
    }
  }

  async function create(data) {
    loading.value = true;
    error.value = null;
    try {
      const res = await ticketsService.create({
        ...data,
        due_date: toYMD(data.due_date),
      });
      invalidateTicketCaches();
      toast("Ticket created");
      await fetchAll({ page: 1 }, { force: true });
      return res.data;
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to create ticket";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, data) {
    const ticketId = Number(id) || id;
    loading.value = true;
    error.value = null;
    try {
      const res = await ticketsService.update(ticketId, {
        ...data,
        due_date: toYMD(data.due_date),
      });
      invalidateTicketCaches(ticketId);
      toast("Ticket updated");
      await fetchAll({ page: pagination.current_page }, { force: true });
      return res.data;
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to update ticket";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    const ticketId = Number(id) || id;
    loading.value = true;
    error.value = null;
    try {
      await ticketsService.delete(ticketId);
      invalidateTicketCaches(ticketId);
      toast("Ticket deleted");
      await fetchAll({ page: 1 }, { force: true });
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to delete ticket";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    error.value = null;
    try {
      await ticketsService.bulkDelete(ids);
      invalidateTicketCaches();
      toast("Tickets deleted");
      await fetchAll({ page: 1 }, { force: true });
    } catch (e) {
      error.value =
        e.response?.data?.message || "Failed to bulk delete tickets";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function addComment(
    ticketId,
    { body, is_internal = false, attachments = [] }
  ) {
    const id = Number(ticketId) || ticketId;
    loading.value = true;
    error.value = null;
    try {
      const res = await ticketsService.addComment(id, {
        body,
        is_internal,
        attachments,
      });
      invalidateTicketCaches(id);
      toast("Comment added");
      await fetchOne(id, { force: true });
      return res.data;
    } catch (e) {
      error.value = e.response?.data?.message || "Failed to add comment";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items,
    item,
    loading,
    error,
    pagination,
    filters,
    byId,
    fetchAll,
    ensureList,
    fetchOne,
    ensureOne,
    create,
    update,
    remove,
    bulkDelete,
    addComment,
    invalidateTicketCaches,
  };
});
